import { useEffect, useState } from "react";

/**
 * Copy of a hook from beta version of ancestry-ui-react.
 * ToDo: Remove after stable release of ancestry-ui-react:1.5.0
 *
 * Reference:
 * https://github.ancestry.com/Ancestry/web-ui-react/blob/master/src/utils/useUIComponentLoaded.ts
 *
 * @param componentName - The name of the UI component to check (e.g., 'modal', 'sidebar', 'alert')
 * @param pollInterval - How often to check for the component in milliseconds (default: 100ms)
 * @param maxRetries - Maximum number of polling attempts before giving up (default: 100, which equals 10 seconds with default interval)
 * @returns Boolean indicating if the component is loaded
 */
export const useUIComponentLoaded = (
    componentName:
        | "accordion"
        | "alert"
        | "autocomplete"
        | "autogrow"
        | "callout"
        | "carousel"
        | "modal"
        | "paging"
        | "sidebar"
        | "switch"
        | "tabs"
        | "validator",
    pollInterval = 1_000,
    maxRetries = 5,
) => {
    const [isLoaded, setIsLoaded] = useState(
        () => typeof window !== "undefined" && !!(window as any).ui?.[componentName],
    );

    useEffect(() => {
        if (isLoaded) return;

        let retryCount = 0;

        const interval = setInterval(() => {
            retryCount++;

            if ((window as any)?.ui?.[componentName]) {
                setIsLoaded(true);
                clearInterval(interval);
            } else if (retryCount >= maxRetries) {
                // Stop polling after maxRetries attempts to prevent infinite looping
                console.warn(
                    `Unable to load '${componentName}' after ${maxRetries} attempts. ` +
                        `Stopping polling to prevent infinite loop. Please ensure the Ancestry UI library is loaded.`,
                );
                clearInterval(interval);
            }
        }, pollInterval);

        return () => clearInterval(interval);
    }, [componentName, isLoaded, pollInterval, maxRetries]);

    return isLoaded;
};
